import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Clock, CheckCircle, CalendarCheck, Loader2, ArrowRight } from 'lucide-react';


// Fallback programs data in case API fails
const fallbackPrograms = [
  {
    id: "healthy-tuesday",
    title: "Healthy Tuesday",
    arabic_title: "الثلاثاء الصحي",
    slug: "healthy-tuesday",
    subtitle: "Weekly Healing Community",
    subtitle_ar: "مجتمع شفاء أسبوعي",
    description: "A weekly in-person healing community focused on emotional awareness, stress release, energy balance, and meaningful connection.",
    description_ar: "مجتمع شفاء أسبوعي حضوري يركز على الوعي العاطفي وتخفيف التوتر وتوازن الطاقة والتواصل الهادف.",
    format: "In-Person · Weekly",
    format_ar: "حضوري · أسبوعي",
    badge: "Free",
    badge_ar: "مجاني",
    focus: ["Emotional awareness", "Stress release", "Energy balance", "Learning & connection"],
    focus_ar: ["الوعي العاطفي", "تخفيف التوتر", "توازن الطاقة", "التعلم والتواصل"],
    outcomes: ["Calm and clarity", "Social confidence", "Sense of belonging", "Emotional resilience"],
    outcomes_ar: ["الهدوء والوضوح", "الثقة الاجتماعية", "الشعور بالانتماء", "المرونة العاطفية"],
    cta: "Join Our Free Community",
    cta_ar: "انضم لمجتمعنا المجاني",
    image_url: "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/yxo3sgur_Healthy%20Tuesday.jpeg",
    sort_order: 0
  },
  {
    id: "ensan-farid",
    title: "Ensan Farid",
    arabic_title: "إنسان فريد",
    slug: "ensan-farid",
    subtitle: "A Unique Being",
    subtitle_ar: "كيان فريد",
    description: "A transformative 3-month identity and emotional mastery journey designed to help you reconnect with your authentic self.",
    description_ar: "رحلة تحولية لمدة 3 أشهر للهوية والإتقان العاطفي مصممة لمساعدتك على إعادة الاتصال بذاتك الحقيقية.",
    format: "Intensive Program · 3 Months",
    format_ar: "برنامج مكثف · 3 أشهر",
    badge: "3-Month Journey",
    badge_ar: "رحلة 3 أشهر",
    focus: ["Self-worth & identity", "Healthy boundaries", "Emotional clarity", "Authentic living"],
    focus_ar: ["تقدير الذات والهوية", "الحدود الصحية", "الوضوح العاطفي", "العيش بأصالة"],
    outcomes: ["Unshakeable confidence", "Emotional independence", "Clear personal boundaries", "Authentic self-expression"],
    outcomes_ar: ["ثقة راسخة", "استقلال عاطفي", "حدود شخصية واضحة", "تعبير ذاتي أصيل"],
    investment: { monthly: "EGP xxxx per month", full: "EGP xxxx — full program payment", prepaid: "EGP xxxx — prepaid discount (pay in advance and save)" },
    investment_ar: { monthly: "xxxx جنيه مصري شهرياً", full: "xxxx جنيه مصري — دفعة البرنامج الكاملة", prepaid: "xxxx جنيه مصري — خصم الدفع المسبق (ادفع مقدماً ووفر)" },
    cta: "Apply Now",
    cta_ar: "تقدم الآن",
    image_url: "https://customer-assets.emergentagent.com/job_crystal-therapy/artifacts/4astk658_Ensan%20Farid.jpeg",
    sort_order: 1
  },
  {
    id: "youm-el-mona",
    title: "Youm El Mona",
    arabic_title: "يوم المنى",
    slug: "youm-el-mona",
    subtitle: "A Day of Wishes",
    subtitle_ar: "يوم الأمنيات",
    description: "A one-day deep healing experience designed for emotional reset, energetic renewal, and conscious pause.",
    description_ar: "تجربة شفاء عميقة ليوم واحد مصممة لإعادة الضبط العاطفي والتجديد الطاقي والتوقف الواعي.",
    format: "Full Day Immersion",
    format_ar: "يوم كامل غامر",
    badge: "Immersive Experience",
    badge_ar: "تجربة غامرة",
    focus: ["Emotional reset", "Energetic renewal", "Conscious pause", "Deep reflection"],
    focus_ar: ["إعادة الضبط العاطفي", "التجديد الطاقي", "التوقف الواعي", "التأمل العميق"],
    outcomes: ["Complete energy renewal", "Emotional release", "Clarity and peace", "Renewed sense of purpose"],
    outcomes_ar: ["تجديد طاقي كامل", "إطلاق عاطفي", "وضوح وسلام", "إحساس متجدد بالهدف"],
    investment: { base: "EGP xxx - one-day experience" },
    investment_ar: { base: "تجربة اليوم الواحد" },
    cta: "Apply Now",
    cta_ar: "تقدم الآن",
    image_url: "https://customer-assets.emergentagent.com/job_crystal-therapy-1/artifacts/z2fkkprc_Youm%20El%20Mona%20Final%20one.jpg",
    sort_order: 2
  },
];

const ProgramsSection = ({ onRegisterClick }) => {
  const { t, isRTL } = useLanguage();
  const [programs, setPrograms] = useState(fallbackPrograms);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch published programs from CMS API
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const programsRes = await fetch(`${backendUrl}/api/cms/programs`);
        if (programsRes.ok) {
          const data = await programsRes.json();
          if (data && data.length > 0) {
            setPrograms(data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
          }
        }
      } catch (err) {
        console.error('Error fetching programs, using fallback:', err);
      }
      setLoading(false);
    };
    fetchAll();
  }, [backendUrl]);

  const handleRegister = (programSlug) => {
    if (onRegisterClick) {
      onRegisterClick(programSlug);
    }
  };

  // Get localized field value
  const getLocalizedField = (program, field) => {
    if (isRTL) {
      return program[`${field}_ar`] || program[field] || '';
    }
    return program[field] || '';
  };

  // Get investment display
  const getInvestment = (program) => {
    const investment = isRTL ? program.investment_ar : program.investment;
    return investment || program.investment;
  };

  // Get custom image positioning for specific programs
  const getImageStyle = (slug) => {
    switch(slug) {
      case 'ensan-farid':
        // Focus on upper portion where the face is located
        return {
          objectFit: 'cover',
          objectPosition: 'center 15%', // Focus on top portion to show face
          transform: 'scale(1.1)' // Zoom in slightly for better face visibility
        };
      case 'youm-el-mona':
        return {
          objectFit: 'cover',
          objectPosition: 'center 30%'
        };
      default:
        return {
          objectFit: 'cover',
          objectPosition: 'center'
        };
    }
  };

  // Loading state
  if (loading) {
    return (
      <section id="programs" className="section-padding bg-charcoal">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
            <span className="ml-3 text-cream-300">{isRTL ? 'جاري التحميل...' : 'Loading programs...'}</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="programs" className="section-padding bg-charcoal">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
          <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
            {t.programs?.tagline || (isRTL ? 'البرامج والتجارب' : 'Programs & Experiences')}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-cream leading-tight mb-4 sm:mb-6 px-2">
            {t.programs?.headline || (isRTL ? 'رحلة شفائك' : 'Your Healing Journey')}
          </h2>
          <div className="gold-divider" />
          <p className="text-cream-300 mt-6 sm:mt-8 px-4 text-sm sm:text-base">
            {t.programs?.description || (isRTL ? 'برامج مصممة بعناية لتلتقي بك حيث أنت وترشدك نحو تحول دائم.' : 'Thoughtfully designed programs to meet you where you are and guide you toward lasting transformation.')}
          </p>
        </div>

        {/* Programs Grid */}
        {programs.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {programs.map((program) => {
              const title = isRTL ? (program.arabic_title || program.title) : program.title;
              const arabicTitle = program.arabic_title;
              const subtitle = getLocalizedField(program, 'subtitle');
              const description = getLocalizedField(program, 'description');
              const format = getLocalizedField(program, 'format');
              const badge = getLocalizedField(program, 'badge');
              const focus = isRTL ? (program.focus_ar || program.focus || []) : (program.focus || []);
              const cta = getLocalizedField(program, 'cta') || (isRTL ? 'سجل الآن' : 'Register Now');
              const investment = getInvestment(program);
              const isFeatured = program.slug === 'healthy-tuesday';

              return (
                <div key={program.id} className={`premium-card group flex flex-col ${isFeatured ? 'ring-1 ring-gold/50 shadow-[0_0_28px_rgba(212,175,55,0.10)]' : ''}`}>
                  <div className="relative overflow-hidden rounded-t-xl aspect-[4/3]">
                    {program.image_url ? (
                      <img src={program.image_url} alt={title} className="w-full h-full group-hover:scale-105 transition-transform duration-700" style={getImageStyle(program.slug)} />
                    ) : (
                      <div className="w-full h-full bg-charcoal-100 flex items-center justify-center">
                        <span className="text-cream-400 text-sm">{isRTL ? 'لا توجد صورة' : 'No image'}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent pointer-events-none" />
                    {badge && (
                      <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                        <span className={`px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs uppercase tracking-wider rounded-md ${badge.toLowerCase() === 'free' || badge === 'مجاني' ? 'bg-gold text-charcoal' : 'bg-charcoal/80 text-gold border border-gold/30'}`}>{badge}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-5 sm:p-6 lg:p-7 flex flex-col flex-1">
                    <div className="mb-3">
                      <h3 className="font-serif text-xl sm:text-2xl text-cream leading-tight">{title}</h3>
                      {!isRTL && arabicTitle && <p className="text-gold/65 text-sm mt-0.5">{arabicTitle}</p>}
                      {isFeatured && <p className="text-gold/80 text-xs tracking-wider mt-1">{isRTL ? '✦ أفضل نقطة بداية لك' : '✦ The easiest way to begin'}</p>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {subtitle && <span className="text-cream-300 text-xs italic">{subtitle}</span>}
                      {subtitle && format && <span className="text-gold/30 select-none">·</span>}
                      {format && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-xs tracking-wide">
                          <Clock size={10} />{format}
                        </span>
                      )}
                    </div>
                    <p className="text-cream-300 text-sm leading-relaxed mb-4">{description}</p>
                    <div className="border-t border-gold/12 mb-4" />
                    {focus.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs uppercase tracking-wider text-gold/70 mb-2">{isRTL ? 'ما الذي ستعملين عليه' : 'What you work on'}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                          {focus.slice(0, 4).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-1.5">
                              <CheckCircle size={11} className="text-gold flex-shrink-0 mt-0.5" />
                              <span className="text-cream-400 text-xs leading-snug">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {investment && Object.values(investment).some(v => v) && (
                      <div className="mt-2 mb-5 pt-4 border-t border-gold/12">
                        <p className="text-xs uppercase tracking-wider text-gold/70 mb-2">{isRTL ? 'الاستثمار' : 'Investment'}</p>
                        {investment.base && <p className="text-gold text-sm font-semibold leading-snug">{investment.base}</p>}
                        {investment.monthly && <p className="text-gold text-sm font-semibold leading-snug">{investment.monthly}</p>}
                        <div className="mt-2 space-y-1">
                          {investment.full && <p className="text-cream-400 text-xs">{investment.full}</p>}
                          {investment.prepaid && <p className="text-cream-300 text-xs font-medium"><span className="text-gold mr-1">★</span>{investment.prepaid}</p>}
                        </div>
                      </div>
                    )}
                    <div className="mt-auto pt-2">
                      <button onClick={() => handleRegister(program.slug)} className={`w-full flex items-center justify-center gap-2 py-3 px-4 text-xs uppercase tracking-wider font-medium transition-all duration-200 active:scale-[0.98] rounded-lg ${badge?.toLowerCase() === 'free' || badge === 'مجاني' ? 'bg-gold text-charcoal hover:bg-gold/90' : 'border border-gold/70 text-gold hover:bg-gold hover:text-charcoal hover:border-gold'}`}>
                        {badge?.toLowerCase() === 'free' || badge === 'مجاني' ? <CalendarCheck size={14} className="text-current flex-shrink-0" /> : <ArrowRight size={14} className="text-current flex-shrink-0" />}
                        <span>{cta}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-cream-400 bg-charcoal-100 border border-white/10 rounded-xl">
            {isRTL ? 'لا توجد برامج متاحة حالياً' : 'No programs available at the moment'}
          </div>
        )}

        {/* Consultation CTA */}
        <div className="text-center p-6 sm:p-8 lg:p-12 border border-gold/20 bg-charcoal-100 rounded-xl">
          <h3 className="font-serif text-xl sm:text-2xl lg:text-3xl text-cream mb-3 sm:mb-4 px-2">
            {t.programs?.consultation?.title || (isRTL ? 'غير متأكدة من أين تبدأين؟' : "Not sure where to start?")}
          </h3>
          <p className="text-cream-300 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base px-4">
            {t.programs?.consultation?.description || (isRTL ? 'احجزي مكالمة استكشافية مجانية ولنجد المسار المثالي لرحلة شفائك.' : "Book a complimentary discovery call and let's find the perfect path for your healing journey.")}
          </p>
          <button
            onClick={() => handleRegister('consultation')}
            className="btn-gold flex items-center gap-2 mx-auto w-full sm:w-auto justify-center rounded-lg"
          >
            <CalendarCheck size={18} className="text-charcoal" />
            <span>{t.programs?.consultation?.cta || (isRTL ? 'احجزي مكالمة' : 'Book Discovery Call')}</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;
